/**
 * The WL-5 invoice page's three writes, each drawn INLINE under the band (no
 * modals): Send, Mark paid and Void. Every refusal is a sentence in the
 * refusal style (wlInvoiceRefusal), never a success.
 *
 *   Send   the recipient is prefilled from GET /recipient (the SAME resolver
 *          the send uses); To and CC are editable. The resolver's own address
 *          is sent as no `to`, so the API records where it came from. A
 *          blocked tax fallback is said, and Send is not offered (the API
 *          would answer `tax_fallback_blocked`). When the mail provider
 *          refuses (502 `email_not_sent`) the invoice is unchanged and the
 *          page says so, with a link to review the invoice and send again
 *          (the refusal's data.reviewPath, fieldiq-api#1359). If the API ever
 *          carries a CUSTOMER review or portal link on that refusal it is
 *          shown too, so the office can pass it on by hand (none today).
 *   Mark paid  Finance's door (PATCH /unified-invoices/:id), offered on a
 *          sent or approved invoice; a draft is refused `invoice_not_sent`.
 *   Void   a required reason; the API then releases the tickets and the page
 *          says which (WlReleasedNotice).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useMarkWlInvoicePaid, useWlInvoiceRecipient, useSendWlInvoice, useVoidWlInvoice } from '../../../hooks/useWlInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wlInvoiceRefusal, wlInvoiceReviewPath, wlInvoiceReviewUrl, wlListWords } from '../../../lib/wireline-slickline-invoices';
import type { WlInvoice } from '../../../types/wireline-slickline-invoices';
import { CcField, parseCcInput } from '../../downhole/sendBits';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError, WlPanel } from '../WlBits';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WlInvoiceSendPanel({ inv, tz, onDone }: { inv: WlInvoice; tz: string | null; onDone: () => void }) {
  const recipient = useWlInvoiceRecipient(inv.id);
  const send = useSendWlInvoice();
  const r = recipient.data?.data ?? null;
  const resolved = r?.to?.email ?? '';
  const [to, setTo] = useState<string | null>(null);
  const [cc, setCc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [reviewPath, setReviewPath] = useState<string | null>(null);
  const toValue = to ?? resolved;
  const ccParsed = parseCcInput(cc);
  const failure = queryLoadFailure(recipient);

  const submit = () => {
    setError(null);
    setReviewUrl(null);
    setReviewPath(null);
    const typed = toValue.trim();
    if (!typed) return setError('Enter an address to send this invoice to.');
    if (!EMAIL_RE.test(typed)) return setError(`"${typed}" is not a valid email address.`);
    if (cc.trim() && ccParsed.problem) return setError(ccParsed.problem);
    send.mutate(
      { id: inv.id, cc: cc.trim() ? ccParsed.cc : undefined, ...(resolved && typed.toLowerCase() === resolved.toLowerCase() ? {} : { to: typed }) },
      {
        onSuccess: (res) => {
          toast.success(`${inv.invoiceNumber} sent to ${res?.sentTo ?? typed}`);
          onDone();
        },
        onError: (e) => {
          setError(wlInvoiceRefusal(e, 'The invoice was not sent.'));
          setReviewUrl(wlInvoiceReviewUrl(e));
          setReviewPath(wlInvoiceReviewPath(e));
        },
      },
    );
  };

  return (
    <WlPanel eyebrow="Send" title={`Email ${inv.invoiceNumber} with its PDF`} testId="wl-invoice-send-panel">
      <div className="space-y-3 px-4 py-4">
        <div data-testid="wl-invoice-recipient" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
          {recipient.isLoading ? (
            'Finding who to send it to…'
          ) : failure ? (
            'Who to send it to could not be read. Enter the address below.'
          ) : r?.to ? (
            <>
              Goes to <span className="font-bold text-white">{r.to.name ? `${r.to.name} <${r.to.email}>` : r.to.email}</span>
              {r.to.sourceLabel ? <span className="text-fieldiq-muted"> — {r.to.sourceLabel}</span> : null}
            </>
          ) : (
            <span>{r?.message ?? 'The customer has no accounting or main contact email. Type an address to send to.'}</span>
          )}
          {r?.lastSent && (
            <p className="mt-1 text-xs text-fieldiq-muted">
              Last sent {r.lastSent.at ? formatInstant(r.lastSent.at, tz) ?? '—' : '—'} to {r.lastSent.to || '—'}
              {r.lastSent.cc && r.lastSent.cc.length ? `, copied to ${r.lastSent.cc.join(', ')}` : ''}
            </p>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <WlField label="To" hint="The address above, unless you type another for this send.">
            <input className={WL_INPUT} type="email" inputMode="email" maxLength={255} value={toValue} onChange={(e) => setTo(e.target.value)} data-testid="wl-invoice-send-to" />
          </WlField>
          <CcField value={cc} onChange={setCc} problem={cc.trim() ? ccParsed.problem : null} />
        </div>
        <WlFormError message={error} />
        {reviewPath && (
          <p data-testid="wl-invoice-send-review-path" className="text-sm text-fieldiq-text">
            <Link className="font-bold underline" to={reviewPath}>Review {inv.invoiceNumber}</Link>, check the address, and send it again.
          </p>
        )}
        {reviewUrl && (
          <p data-testid="wl-invoice-send-review-url" className="text-sm text-fieldiq-text">
            The customer's review link, to pass on by hand:{' '}
            <a className="break-all font-mono underline" href={reviewUrl} target="_blank" rel="noreferrer">{reviewUrl}</a>
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={WL_SECONDARY} onClick={onDone} disabled={send.isPending}>Cancel</button>
          <button type="button" className={WL_PRIMARY} onClick={submit} disabled={send.isPending} data-testid="wl-invoice-send-confirm">
            <Send className="size-4" aria-hidden="true" />
            {send.isPending ? 'Sending…' : 'Send with PDF'}
          </button>
        </div>
      </div>
    </WlPanel>
  );
}

/** Record the customer's payment. Final: a paid invoice cannot be sent or voided. */
export function WlInvoiceMarkPaidPanel({ inv, onDone }: { inv: WlInvoice; onDone: () => void }) {
  const markPaid = useMarkWlInvoicePaid();
  const [error, setError] = useState<string | null>(null);
  return (
    <div data-testid="wl-invoice-paid-confirm" className="space-y-3 rounded-fi-control border border-fi-amber/50 bg-fieldiq-card px-4 py-3">
      <WlFormError message={error} />
      <p className="text-sm text-fieldiq-text">
        Record that {inv.customerName || 'the customer'} paid {fmtCurrency(inv.total, inv.currency)}. A paid invoice is final: it cannot be sent or voided.
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onDone} disabled={markPaid.isPending}>Not now</button>
        <button
          type="button"
          className={WL_PRIMARY}
          disabled={markPaid.isPending}
          data-testid="wl-invoice-paid-submit"
          onClick={() => {
            setError(null);
            markPaid.mutate(inv.id, {
              onSuccess: () => { toast.success(`${inv.invoiceNumber} marked paid`); onDone(); },
              onError: (e) => setError(wlInvoiceRefusal(e, 'The invoice was not marked paid.')),
            });
          }}
        >
          <CheckCircle2 className="size-4" aria-hidden="true" />
          {markPaid.isPending ? 'Saving…' : 'Mark paid'}
        </button>
      </div>
    </div>
  );
}

/**
 * VOID NEEDS A REASON. The button stays enabled so a blank press is SAID
 * (the API's `reason_required` sentence), and nothing is sent until a reason
 * is typed.
 */
export function WlInvoiceVoidPanel({ inv, onDone, onVoided }: { inv: WlInvoice; onDone: () => void; onVoided: (releasedIds: string[]) => void }) {
  const voidInvoice = useVoidWlInvoice();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const live = inv.tickets.filter((t) => !t.releasedAt).map((t) => t.ticketNumber);
  return (
    <div data-testid="wl-invoice-void-confirm" className="space-y-3 rounded-fi-control border border-fi-amber/50 bg-fieldiq-card px-4 py-3">
      <WlFormError message={error} />
      <p className="text-sm text-fieldiq-text">
        Void {inv.invoiceNumber}? A void invoice is kept for the record and cannot be sent, paid or reopened.
        {live.length ? ` ${wlListWords(live)} ${live.length === 1 ? 'goes' : 'go'} back to the tickets waiting to be invoiced.` : ''}
      </p>
      <WlField label="Reason *">
        <textarea className={`${WL_INPUT} h-24 py-2`} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="wl-invoice-void-reason" />
      </WlField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onDone} disabled={voidInvoice.isPending}>Keep invoice</button>
        <button
          type="button"
          disabled={voidInvoice.isPending}
          data-testid="wl-invoice-void-submit"
          className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => {
            setError(null);
            if (!reason.trim()) return setError('A reason is required to void an invoice.');
            voidInvoice.mutate(
              { id: inv.id, reason: reason.trim() },
              {
                onSuccess: (res) => { toast.success(`${inv.invoiceNumber} voided`); onVoided(res.releasedTicketIds); onDone(); },
                onError: (e) => setError(wlInvoiceRefusal(e, 'The invoice was not voided.')),
              },
            );
          }}
        >
          <Ban className="size-4" aria-hidden="true" />
          {voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
        </button>
      </div>
    </div>
  );
}

/** Said after a void: which tickets went back to waiting to be invoiced. A notice, not a success: amber edge, role="status". */
export function WlReleasedNotice({ numbers, onDismiss }: { numbers: string[]; onDismiss: () => void }) {
  return (
    <div role="status" data-testid="wl-invoice-released" className="flex items-start gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <p className="flex-1">
        {numbers.length
          ? `Released ${wlListWords(numbers)}. ${numbers.length === 1 ? 'It is' : 'They are'} waiting to be invoiced again and can go on a new invoice.`
          : 'No field ticket was released: none was still on this invoice.'}
      </p>
      <button type="button" className="text-xs font-bold underline" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}
